import {
  Card,
  CardHeader,
  CardContent,
  Stack,
  TextField,
  CardActions,
  Modal,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch, useSelector } from 'react-redux';
import { branchSchema } from '../schemas/branchSchemas';
import { updateBranch, resetBranchState } from '../store/branchActions';

BranchEditForm.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  branch: PropTypes.object,
};

export default function BranchEditForm({ branch, open, onClose }) {
  const dispatch = useDispatch();
  const { isLoading, isSuccess } = useSelector((state) => state.branchReducer);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      id: '',
      name: '',
      code: '',
      country_code: '',
      is_active: true,
    },
    resolver: yupResolver(branchSchema),
  });

  useEffect(() => {
    if (branch) {
      reset({
        id: branch?.id || '',
        name: branch?.name || '',
        code: branch?.code || '',
        country_code: branch?.country_code || '',
        is_active: branch?.is_active || true,
      });
    }
  }, [branch, reset]);

  useEffect(() => {
    if (isSuccess) {
      onClose();
    }
  }, [isSuccess, onClose]);

  const onSubmit = async (values) => {
    if (!values?.id) return;
    const payload = {
      id: values?.id,
      payload: values,
    };
    dispatch(updateBranch(payload));
  };

  const handleClose = () => {
    reset();
    dispatch(resetBranchState());
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Card
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '340px', md: '600px' },
          bgcolor: 'background.paper',
          borderRadius: '10px',
          boxShadow: 24,
          p: { xs: '0px', md: '2px' },
          alignItems: 'center',
        }}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <CardHeader title="Edit Location" />
          <CardContent>
            <Stack spacing={3}>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type="text"
                    label="Name"
                    error={Boolean(errors.name)}
                    helperText={errors.name?.message}
                  />
                )}
              />

              <Controller
                name="code"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type="text"
                    label="Code"
                    error={Boolean(errors.code)}
                    helperText={errors.code?.message}
                  />
                )}
              />

              <Controller
                name="country_code"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={Boolean(errors.country_code)}>
                    <InputLabel>Country Code</InputLabel>
                    <Select {...field} label="Country Code">
                      <MenuItem value="254">Kenya (254)</MenuItem>
                      <MenuItem value="255">Uganda (255)</MenuItem>
                      <MenuItem value="256">Tanzania (256)</MenuItem>
                    </Select>
                    {errors.country_code && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                        {errors.country_code.message}
                      </Typography>
                    )}
                  </FormControl>
                )}
              />

              <Controller
                name="is_active"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Switch {...field} checked={field.value} color="primary" />}
                    label="Active"
                  />
                )}
              />
            </Stack>
          </CardContent>
          <CardActions sx={{ p: 3, pt: 0 }}>
            <Stack direction="row" spacing={2} sx={{ width: '100%', justifyContent: 'flex-end' }}>
              <LoadingButton variant="outlined" onClick={handleClose} disabled={isLoading}>
                Cancel
              </LoadingButton>
              <LoadingButton type="submit" variant="contained" loading={isLoading} disabled={isSubmitting}>
                Save Changes
              </LoadingButton>
            </Stack>
          </CardActions>
        </form>
      </Card>
    </Modal>
  );
}
